import { confirmProfileVerification, initializeProfileVerification } from '@/api/backend/profile';
import { CompanyProfileType, InvestorProfileType } from '@/api/backend/types';
import { FormErrorAlert, FormErrorAlertProps } from '@/components/form-error-alert';
import { useCountdownTimer } from '@/hooks/use-countdown-timer';
import { formatIsoDate, getLocaleObject } from '@/lib/utils';
import { Validators } from '@/lib/validator';
import { queryClient } from '@/services/queryClient';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  CalendarInput,
  FormError,
  FormField,
  FormLabel,
  FormLabelGroup,
  Input,
  Link,
  useToast,
} from '@opaz/ui-components';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { useState } from 'react';
import { SubmitHandler, useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

type ProfileVerificationProps = {
  profile: InvestorProfileType | CompanyProfileType;
  onSuccess: () => void;
};

interface VerificationInput {
  civilNumber: string;
  idCardExpiryDate: Date;
}

interface VerificationCodeInput {
  code: string;
}

const verificationFormSchema = z.object({
  civilNumber: z
    .string()
    .min(1)
    .refine(...Validators.stringIsNumber),
  idCardExpiryDate: z.date(),
});

const verificationCodeSchema = z.object({
  code: z
    .string()
    .length(6)
    .refine(...Validators.stringIsNumber),
});

const RESEND_CODE_TIMEOUT = 60;

export const ProfileVerification = ({ profile, onSuccess }: ProfileVerificationProps) => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();

  const [isVerificationCodeMode, setIsVerificationCodeMode] = useState(false);
  const [formError, setFormError] = useState<FormErrorAlertProps['error']>(null);
  const [verificationData, setVerificationData] = useState<{ civilNumber: string; idCardExpiryDate: Date } | null>(
    null,
  );

  const { formatTime, isFinished, startTimer } = useCountdownTimer(RESEND_CODE_TIMEOUT, false);
  const resendDisabled = !isFinished && isVerificationCodeMode;

  const {
    control,
    register: registerVerificationForm,
    formState: { errors: verificationFormErrors },
    handleSubmit: handleVerificationFormSubmit,
  } = useForm<VerificationInput>({
    resolver: zodResolver(verificationFormSchema),
  });

  const { field: expiryDateField } = useController({
    control,
    name: 'idCardExpiryDate',
  });

  const {
    register: registerCodeForm,
    formState: { errors: codeFormErrors },
    handleSubmit: handleCodeFormSubmit,
  } = useForm<VerificationCodeInput>({
    resolver: zodResolver(verificationCodeSchema),
  });

  const { mutateAsync: initializeVerification, isPending: isInitializePending } = useMutation({
    mutationFn: async (data: VerificationInput) => {
      await initializeProfileVerification({
        civilNumber: data.civilNumber,
        idCardExpiryDate: data.idCardExpiryDate,
      });
      return data;
    },
    onSuccess: (data) => {
      setVerificationData(data);
      setFormError(null);
      setIsVerificationCodeMode(true);
      startTimer();
    },
    onError: (error: AxiosError) => {
      setFormError(error);
    },
  });

  const { mutateAsync: confirmVerification, isPending: isConfirmPending } = useMutation({
    mutationFn: async (data: { civilNumber: string; idCardExpiryDate: Date; code: string }) => {
      await confirmProfileVerification({
        civilNumber: data.civilNumber,
        idCardExpiryDate: data.idCardExpiryDate,
        code: data.code,
      });
    },
    onSuccess: () => {
      onSuccess();
      toast({
        message: t('profile.verification.success'),
        variant: 'success',
      });

      queryClient.invalidateQueries({
        queryKey: ['userProfileDetails', profile.id],
      });

      queryClient.invalidateQueries({
        queryKey: ['userProfile'],
      });

      setFormError(null);
    },
    onError: (error: AxiosError) => {
      setFormError(error);
    },
  });

  const onVerificationSubmit: SubmitHandler<VerificationInput> = async (data) => {
    await initializeVerification(data);
  };

  const onCodeSubmit: SubmitHandler<VerificationCodeInput> = async (data) => {
    if (!verificationData) return;
    await confirmVerification({
      ...verificationData,
      code: data.code,
    });
  };

  const handleResendCode = async () => {
    if (!verificationData) return;
    startTimer();
    await initializeVerification(verificationData);
  };

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-foreground text-3xl font-bold">{t('profile.verification.title')}</h1>

      <FormErrorAlert error={formError} />

      <>
        <p className="whitespace-pre-line">{t('profile.verification.instructions')}</p>
        <form onSubmit={handleVerificationFormSubmit(onVerificationSubmit)} className="flex flex-col gap-5 max-w-md">
          <FormField>
            <FormLabelGroup>
              <FormLabel error={!!verificationFormErrors.civilNumber}>
                {t('profile.verification.fields.civilId')}
              </FormLabel>

              {verificationFormErrors.civilNumber && (
                <FormError>
                  {t(verificationFormErrors.civilNumber.message as unknown as TemplateStringsArray)}
                </FormError>
              )}
            </FormLabelGroup>

            <Input
              className="w-full"
              error={!!verificationFormErrors.civilNumber}
              disabled={isInitializePending || isVerificationCodeMode}
              {...registerVerificationForm('civilNumber')}
            />
          </FormField>

          <FormField>
            <FormLabelGroup>
              <FormLabel error={!!verificationFormErrors.idCardExpiryDate}>
                {t('profile.verification.fields.expiryDate')}
              </FormLabel>
              {verificationFormErrors.idCardExpiryDate && (
                <FormError>
                  {t(verificationFormErrors.idCardExpiryDate.message as unknown as TemplateStringsArray)}
                </FormError>
              )}
            </FormLabelGroup>

            <CalendarInput
              locale={getLocaleObject(i18n.language)}
              dateFormatter={(date) => {
                return formatIsoDate(date.toISOString(), i18n.language);
              }}
              value={expiryDateField.value ? dayjs(expiryDateField.value).toDate() : undefined}
              onChange={expiryDateField.onChange}
              placeholder={t('common.selectDate')}
              disabled={isInitializePending || isVerificationCodeMode}
              error={!!verificationFormErrors.idCardExpiryDate}
              calendarProps={{
                mode: 'single',
                disabled: {
                  before: new Date(),
                },
              }}
            />
          </FormField>

          {!isVerificationCodeMode && (
            <div className="mt-8">
              <Button type="submit" disabled={isInitializePending}>
                {t('profile.verification.verifyButton')}
              </Button>
            </div>
          )}
        </form>
      </>

      {isVerificationCodeMode && (
        <>
          <p className="mt-4">{t('profile.verification.codeVerification.instructions')}</p>

          <form onSubmit={handleCodeFormSubmit(onCodeSubmit)} className="flex flex-col gap-5 max-w-md">
            <FormField>
              <FormLabelGroup>
                <FormLabel error={!!codeFormErrors.code}>
                  {t('profile.verification.codeVerification.codeField')}
                </FormLabel>
                {codeFormErrors.code && (
                  <FormError>{t(codeFormErrors.code.message as unknown as TemplateStringsArray)}</FormError>
                )}
              </FormLabelGroup>

              <Input
                className="w-full"
                error={!!codeFormErrors.code}
                disabled={isConfirmPending}
                maxLength={6}
                {...registerCodeForm('code')}
              />
            </FormField>

            <div className="flex flex-col items-start gap-2">
              <span className="font-medium">{!isFinished ? formatTime() : ''}</span>

              <span>
                {t('profile.verification.codeVerification.notReceived')}{' '}
                <Link type="button" onClick={handleResendCode} disabled={resendDisabled}>
                  {t('profile.verification.codeVerification.resend')}
                </Link>
              </span>
            </div>

            <div className="mt-8">
              <Button className="flex-1" type="submit" disabled={isConfirmPending}>
                {t('profile.verification.codeVerification.confirmButton')}
              </Button>
            </div>
          </form>
        </>
      )}
    </div>
  );
};
